import { useMemo, useState, type KeyboardEvent } from "react";
import {
  MultiSelect,
  type MultiSelectChangeEvent,
  type MultiSelectProps,
} from "primereact/multiselect";
import { Button } from "primereact/button";
import { FilterService } from "primereact/api";
import type { SelectItem } from "primereact/selectitem";

export type CreatableMultiSelectProps = MultiSelectProps;

// PrimeReact's MultiSelect can't create options on the fly, so this wrapper
// tracks user-added options locally. When the filter text matches nothing, the
// value can be added via the button in the empty-filter message or by pressing
// Enter in the filter input (Tab can't reach the panel button, so the Enter
// path is what keeps this keyboard-accessible).
export function CreatableMultiSelect({
  options = [],
  value,
  onChange,
  pt,
  ...multiSelectProps
}: CreatableMultiSelectProps) {
  const [customOptions, setCustomOptions] = useState<SelectItem[]>([]);
  const [filterValue, setFilterValue] = useState("");

  const allOptions = useMemo(() => {
    const known = [...options, ...customOptions];
    // selected values saved outside the known lists (e.g. via the old
    // free-text "Other" flow) still need an option so their chips get labels
    const unlisted = (value || [])
      .filter((v: string) => !known.some(option => option.value === v))
      .map((v: string) => ({ label: v, value: v }));

    return [...known, ...unlisted];
  }, [options, customOptions, value]);

  const query = filterValue.trim();
  const hasMatches = FilterService.filter(allOptions, ["label"], query, "contains").length > 0;

  const addFilterValueAsOption = () => {
    if (!query) return;

    const newValue = query.toLowerCase().replace(/\s/g, "_");
    if (!allOptions.some(option => option.value === newValue)) {
      setCustomOptions(prev => [...prev, { label: query, value: newValue }]);
    }
    if (!value.includes(newValue)) {
      onChange?.({ value: [...value, newValue] } as MultiSelectChangeEvent);
    }
  };

  const onFilterInputKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && query && !hasMatches) {
      addFilterValueAsOption();
    }
  };

  return (
    <MultiSelect
      {...multiSelectProps}
      value={value}
      options={allOptions}
      onChange={onChange}
      filter
      resetFilterOnHide
      onFilter={e => setFilterValue(e.filter)}
      onHide={() => setFilterValue("")}
      emptyFilterMessage={
        <Button
          type="button"
          link
          className="p-0"
          label={`Add "${query}"`}
          onClick={addFilterValueAsOption}
        />
      }
      pt={{ ...pt, filterInput: { root: { onKeyDown: onFilterInputKeyDown } } }}
    />
  );
}
